import { CalendarDays, CalendarCheck, Info, BellRing } from "lucide-react";
import { BUSINESS } from "../data/business";

const MONTHLY = [
    {
        day: "07",
        title: "FGTS dos funcionários",
        detail: "Recolhimento da folha do mês anterior.",
        tags: ["Com funcionários"],
    },
    {
        day: "10",
        title: "ISS sobre serviços",
        detail: "Imposto municipal sobre serviços (data varia por município).",
        tags: ["Lucro Presumido", "Lucro Real"],
    },
    {
        day: "15",
        title: "DCTFWeb",
        detail: "Declaração dos tributos sobre a folha de pagamento.",
        tags: ["Com funcionários"],
    },
    {
        day: "20",
        title: "DAS — Simples Nacional, MEI e ICMS",
        detail: "Guia do Simples, valor fixo do MEI e ICMS (varia por estado).",
        tags: ["MEI", "Simples Nacional", "Comércio e Indústria"],
    },
    {
        day: "25",
        title: "PIS e COFINS",
        detail: "Contribuições federais sobre o faturamento.",
        tags: ["Lucro Presumido", "Lucro Real"],
    },
];

const YEARLY = [
    {
        month: "Março",
        title: "DEFIS",
        detail: "Declaração anual das empresas do Simples Nacional.",
        tags: ["Simples Nacional"],
    },
    {
        month: "Maio",
        title: "DASN-SIMEI e ECD",
        detail: "Declaração anual do MEI e Escrituração Contábil Digital.",
        tags: ["MEI", "Lucro Presumido", "Lucro Real"],
    },
    {
        month: "Julho",
        title: "ECF",
        detail: "Apuração anual do IRPJ e da CSLL do ano anterior.",
        tags: ["Lucro Presumido", "Lucro Real"],
    },
];

export const AgendaTributaria = () => {
    return (
        <section id="agenda" data-testid="agenda-section" className="py-20 lg:py-32 bg-[#F8FAFC]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl reveal-on-scroll">
                    <p className="eyebrow text-xs font-bold uppercase tracking-[0.2em] text-blue-900" data-testid="agenda-eyebrow">
                        Agenda tributária
                    </p>
                    <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="agenda-title">
                        Os prazos que sua empresa não pode perder
                    </h2>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed" data-testid="agenda-subtitle">
                        Todo mês e todo ano o fisco espera guias e declarações. Veja as principais datas — e nunca
                        mais se preocupe com nenhuma delas.
                    </p>
                </div>

                <div className="mt-14 grid lg:grid-cols-2 gap-8">
                    <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 transition-shadow duration-300 hover:shadow-lg" data-testid="agenda-monthly">
                        <div className="flex items-center gap-3 mb-8">
                            <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-slate-900 text-amber-400">
                                <CalendarDays className="w-5 h-5" />
                            </span>
                            <h3 className="text-xl sm:text-2xl font-medium text-slate-900">Todo mês</h3>
                        </div>
                        <ul className="space-y-6">
                            {MONTHLY.map((item) => (
                                <li key={item.day} className="flex gap-5" data-testid={`agenda-monthly-${item.day}`}>
                                    <span className="flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-blue-50 border border-blue-200 shrink-0">
                                        <span className="text-[9px] font-bold uppercase tracking-widest text-blue-900">dia</span>
                                        <span className="text-xl font-bold text-blue-900 leading-none">{item.day}</span>
                                    </span>
                                    <div className="min-w-0">
                                        <h4 className="text-base font-semibold text-slate-900">{item.title}</h4>
                                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{item.detail}</p>
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {item.tags.map((tag) => (
                                                <span key={tag} className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 bg-[#F8FAFC] border border-slate-200 rounded-full px-2.5 py-1">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex flex-col gap-8">
                        <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 transition-shadow duration-300 hover:shadow-lg" data-testid="agenda-yearly">
                            <div className="flex items-center gap-3 mb-8">
                                <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-slate-900 text-amber-400">
                                    <CalendarCheck className="w-5 h-5" />
                                </span>
                                <h3 className="text-xl sm:text-2xl font-medium text-slate-900">Todo ano</h3>
                            </div>
                            <ul className="space-y-6">
                                {YEARLY.map((item) => (
                                    <li key={item.month} className="flex gap-5" data-testid={`agenda-yearly-${item.month.toLowerCase()}`}>
                                        <span className="flex items-center justify-center h-14 min-w-14 px-3 rounded-xl bg-blue-50 border border-blue-200 shrink-0">
                                            <span className="text-sm font-bold text-blue-900">{item.month}</span>
                                        </span>
                                        <div className="min-w-0">
                                            <h4 className="text-base font-semibold text-slate-900">{item.title}</h4>
                                            <p className="mt-1 text-sm text-slate-600 leading-relaxed">{item.detail}</p>
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {item.tags.map((tag) => (
                                                    <span key={tag} className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 bg-[#F8FAFC] border border-slate-200 rounded-full px-2.5 py-1">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-slate-900 rounded-2xl p-8 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="agenda-cta">
                            <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-amber-500/20 text-amber-400 shrink-0">
                                <BellRing className="w-6 h-6" />
                            </span>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-white">Nunca mais perca um prazo</h3>
                                <p className="mt-1 text-sm text-slate-300 leading-relaxed">
                                    Clientes WBS têm os prazos acompanhados pelo contador, com entregas garantidas.
                                </p>
                            </div>
                            <a
                                href={`${BUSINESS.whatsapp}?text=${encodeURIComponent("Olá! Quero que a WBS cuide da agenda tributária da minha empresa.")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="agenda-cta-button"
                                className="shrink-0 inline-flex items-center justify-center rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm px-6 py-3.5 transition-transform duration-200 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                            >
                                Deixar com a WBS
                            </a>
                        </div>
                    </div>
                </div>

                <p className="mt-8 flex items-start gap-3 text-xs text-slate-500 leading-relaxed" data-testid="agenda-disclaimer">
                    <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-900" />
                    As datas seguem o calendário federal e podem variar conforme município, estado, feriados e
                    prorrogações oficiais. Esta agenda é informativa — clientes WBS recebem as datas exatas da sua empresa.
                </p>
            </div>
        </section>
    );
};
